import React, { useState } from "react";
import { Edit, Trash2, Building2, Calendar, DollarSign, Eye } from "lucide-react";
import PartnershipDetailsModal from "./PartnershipDetailsModal";
import { Button } from "@/Components/ui/button";
import { AuthService } from "../../Entities/AuthService";
import { Badge } from "@/Components/ui/badge";
import { Card, CardContent } from "@/Components/ui/card";
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

const PartnershipsList = ({ partnerships, onEdit, onDelete, isLoading }) => {
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const isObserver = AuthService.isObserver(); // ✅ Verificar se é observador
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : '';
    } catch {
      return '';
    }
  };

  const getStatusColor = (status)=> {
    switch (status) {
      case 'no_prazo':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'quitado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'em_debito':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'suspenso':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'verificar':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'no_prazo':
        return 'No Prazo';
      case 'quitado':
        return 'Quitado';
      case 'em_debito':
        return 'Em Débito';
      case 'verificar':
        return 'Verificar';
      default:
        return 'Indefinido';
    }
  };

  const getTipoParceiraColor = (tipo) => {
    switch (tipo) {
      case 'Termo de Colaboração':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Termo de Colaboração - Emenda Impositiva':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Termo de Fomento':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Termo de Fomento - Emenda Impositiva':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Acordo de Cooperação':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'Subvenção Social':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-6 bg-white animate-pulse">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Informações principais */}
              <div className="flex-1 space-y-3">
                {/* Título e badges */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="h-6 bg-slate-200 rounded w-64"></div>
                  <div className="flex gap-2">
                    <div className="h-6 bg-slate-200 rounded w-20"></div>
                    <div className="h-6 bg-slate-200 rounded w-32"></div>
                  </div>
                </div>

                {/* Grid de informações */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                  <div className="h-4 bg-slate-200 rounded w-40"></div>
                  <div className="h-4 bg-slate-200 rounded w-24"></div>
                  <div className="h-4 bg-slate-200 rounded w-36"></div>
                  <div className="h-4 bg-slate-200 rounded w-28"></div>
                  <div className="h-4 bg-slate-200 rounded w-28"></div>
                </div>

                {/* Observações */}
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                </div>
              </div>

              {/* Ações */}
              <div className="flex flex-row lg:flex-col gap-2">
                <div className="h-9 bg-slate-200 rounded w-24"></div>
                <div className="h-9 bg-slate-200 rounded w-24"></div>
                <div className="h-9 bg-slate-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (partnerships.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            Nenhuma parceria encontrada
          </h3>
          <p className="text-slate-500">
            Não há parcerias que correspondam aos filtros aplicados.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {partnerships.map((partnership) => (
          <Card key={partnership.id} className="hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Informações principais */}
                <div className="flex-1 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {partnership.osc}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge className={getStatusColor(partnership.status || partnership.status_automatico)}>
                        {getStatusLabel(partnership.status || partnership.status_automatico)}
                      </Badge>
                      {partnership.tipo_parceria && (
                        <Badge variant="outline" className={getTipoParceiraColor(partnership.tipo_parceria)}>
                          {partnership.tipo_parceria}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-600">Processo:</span>
                      <span className="text-slate-900">{partnership.processo_ano}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-600">CNPJ:</span>
                      <span className="text-slate-900 font-mono text-xs">
                        {partnership.cnpj}
                      </span>
                    </div>

                    {partnership.ano && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-900">{partnership.ano}</span>
                      </div>
                    )}

                    {partnership.valor && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="text-green-700 font-semibold">
                          {formatCurrency(partnership.valor)}
                        </span>
                      </div>
                    )}

                    {partnership.data_inicio && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-600">Início:</span>
                        <span className="text-slate-900">
                          {formatDate(partnership.data_inicio)}
                        </span>
                      </div>
                    )}

                    {partnership.data_fim && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-600">Fim:</span>
                        <span className="text-slate-900">
                          {formatDate(partnership.data_fim)}
                        </span>
                      </div>
                    )}
                  </div>

                  {partnership.analises_observacoes && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700 line-clamp-2">
                        <span className="font-medium">Observações: </span>
                        {partnership.analises_observacoes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-row lg:flex-col gap-2 lg:ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPartnership(partnership)}
                    className="flex items-center gap-2 hover:bg-purple-50 hover:border-purple-300 text-purple-600 hover:text-purple-700"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">Detalhes</span>
                  </Button>
                  {!isObserver && ( // ⛔ Ocultar para observadores
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(partnership)}
                        className="flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">Editar</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(partnership)}
                        className="flex items-center gap-2 hover:bg-red-50 hover:border-red-300 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Excluir</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal de Detalhes */}
      {selectedPartnership && (
        <PartnershipDetailsModal
          partnership={selectedPartnership}
          onClose={() => setSelectedPartnership(null)}
        />
      )}
    </>
  );
};

export default PartnershipsList;